import { z } from 'zod';

import { UuidSchema } from '../../common/id/id';

/**
 * Acknowledging how far the member has read.
 *
 * **A message id, not a timestamp.** The client names a row it actually has,
 * and the server resolves that row's `sentAt` itself. A client-supplied instant
 * would be a client-supplied clock (N3) — and a device whose clock runs fast
 * would mark messages read that it has never received.
 *
 * The id is checked against the conversation, so it cannot be used to move
 * somebody's read mark using a message from a different chat.
 */
export const ReadReceiptSchema = z
  .object({
    /** The newest message the member has seen. */
    lastReadMessageId: UuidSchema,
  })
  .strict();

export type ReadReceipt = z.infer<typeof ReadReceiptSchema>;

/**
 * What the acknowledgement answers with.
 *
 * It returns the **resolved** state rather than an empty 204, because the mark
 * only ever moves forward (a claim, N27): two devices acknowledging out of order
 * must both be told where the mark actually ended up, or the one that lost the
 * race would draw a stale badge until something else refreshed it.
 */
export const ReadReceiptResultSchema = z
  .object({
    lastReadAt: z.iso.datetime(),
    /** What remains unread after this acknowledgement. `0` in the ordinary case. */
    unreadCount: z.number().int().min(0),
  })
  .strict();

export type ReadReceiptResult = z.infer<typeof ReadReceiptResultSchema>;
